<template>
  <div class="relative w-full h-full">
    <div id="tru-map" class="w-full h-full touch-none"></div>
    <MapPopOver
      v-if="
        search.selectedStore &&
        search.selectedStore.isProfileNavigable === true &&
        search.selectedStore.truRating &&
        search.selectedStore.truRating.length > 0 &&
        search.selectedStore.ratingCount &&
        search.selectedStore.ratingCount > 0
      "
      v-show="ui.showMap"
      :store="search.selectedStore"
      @close="map.selectedFeatureId = null"
    />
    <MapPopOverHovered
      :store="search.hoveredStore"
      @is-outside="onHoverOverlayIsOutside"
      @close="map.hoveredFeatureId = null"
    />
  </div>
</template>

<script setup lang="ts">
import Feature, { type FeatureLike } from 'ol/Feature'
import Map from 'ol/Map'
import Overlay from 'ol/Overlay'
import View from 'ol/View'
import { defaults as defaultControls } from 'ol/control'
import { click } from 'ol/events/condition'
import { boundingExtent } from 'ol/extent'
import { Point } from 'ol/geom'
import { defaults as defaultInteractions } from 'ol/interaction'
import LinkInteraction from 'ol/interaction/Link'
import Select from 'ol/interaction/Select'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import { useGeographic } from 'ol/proj'
import { Cluster } from 'ol/source'
import OSM from 'ol/source/OSM'
import VectorSource from 'ol/source/Vector'
import { Fill, Icon, Stroke, Style, Text } from 'ol/style'
import type { MapCoordinate } from '~/types'

useGeographic()
const config = useRuntimeConfig()
const map = useMapStore()
const ui = useUiStore()
const user = useUserStore()
const search = useSearchStore()
const geocoded = useGeocodedStore()
const gtm = useGtm()
const { isDesktop } = useDevice()

let olMapRef: Map
let storeSelectedOverlay: Overlay
const isPinCentering = ref(false)
const { start: startOverlayTimeout, stop: stopOverlayTimeout } = useTimeoutFn(() => {
  map.hoverOverlayIsOutside = true
  map.hoveredFeatureId = null
}, 500)

const clusterSource = new Cluster({
  distance: 40,
  source: new VectorSource({
    features: []
  })
})

const vectorClusterLayer = new VectorLayer({
  source: clusterSource
})

function onHoverOverlayIsOutside(isOutside: boolean) {
  map.hoverOverlayIsOutside = isOutside
  if (!isOutside) stopOverlayTimeout()
}

onMounted(async () => {
  try {
    await user.initialise()
  } catch (e) {
    console.warn('User initialization failed', e)
  }

  map.initialise(
    user.coordinates,
    user.useMyLocation
      ? config.public.map.userPositionZoomLevel
      : geocoded.geocodedPlace
        ? geocoded.geocodedPlace.zoomLevel
        : config.public.map.defaultZoomLevel
  )

  storeSelectedOverlay = new Overlay({
    element: document.getElementById('map-popover')!,
    autoPan: true,
    autoPanAnimation: {
      duration: 250
    },
    positioning: 'bottom-center',
    offset: [0, -210]
  })

  const storeHoveredOverlay = new Overlay({
    element: document.getElementById('map-popover-hovered')!
  })

  vectorClusterLayer.setStyle(f => {
    const feature = f as Feature
    const features = getFeatures(feature)
    const count = features.length
    const isCluster = features.length > 1

    feature.set('isCluster', isCluster)
    feature.set('count', count)

    if (isCluster) {
      const isSelected = features.some(x => (x.getId() as number) === map.selectedFeatureId)
      const style = getMapMarker(isSelected ? 'map_marker_cluster_selected' : 'map_marker_cluster')
      style.setText(
        new Text({
          font: '600 13px "Open Sans"',
          text: count > 99 ? '99+' : count.toString(),
          fill: new Fill({ color: 'white' }),
          stroke: new Stroke({ color: 'black', width: 1 })
        })
      )
      return style
    }

    const featureId = getFeature(feature).getId() as number
    return featureId === map.selectedFeatureId ? getMapMarker('map_marker_selected') : getMapMarker('map_marker')
  })

  const selectClick = new Select({
    condition: click,
    filter: f => !isClusteredFeature(f),
    style: getMapMarker('map_marker_selected')
  })
  selectClick.on('select', evt => {
    if (evt.selected.length > 0) {
      map.hoveredFeatureId = null
      const feature = evt.selected[0].get('features')[0] as Feature
      const id = feature.getId() as number
      map.selectedFeatureId = id

      const store = search.getStoreById(id)
      gtm?.trackEvent({
        event: 'Store Marker Selected',
        category: 'discovery',
        action: 'Select Store Marker',
        label: store.displayName
      })
      ui.showMapView()
    } else {
      map.selectedFeatureId = null
      map.navigateTo = undefined
    }
  })

  olMapRef = initMap(map.center, map.zoom)
  olMapRef.addOverlay(storeHoveredOverlay)
  olMapRef.addOverlay(storeSelectedOverlay)
  olMapRef.addInteraction(selectClick)

  // Single-click on a cluster centers it over its pins
  olMapRef.on('singleclick', evt => {
    const features = evt.map.getFeaturesAtPixel(evt.pixel)
    if (features.length > 0) {
      const subFeatures = features[0].get('features') as Feature[]
      if (subFeatures?.length > 1) {
        isPinCentering.value = true
        const extent = boundingExtent(subFeatures.map(f => f.getGeometry()!.getFlatCoordinates()))
        const center: MapCoordinate = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2]
        olMapRef.getView().animate({ center, duration: 500 })
        const onAnimEnd = () => {
          olMapRef.un('moveend', onAnimEnd)
          isPinCentering.value = false
        }
        olMapRef.on('moveend', onAnimEnd)
      }
    }
  })

  olMapRef.once('rendercomplete', () => {
    map.onRenderComplete()
    search.performSearch().then(() => {
      if (search.stores.length > 0) ui.showListView()
    })
  })

  olMapRef.on('movestart', () => {
    if (!isPinCentering.value) map.onMoveStart()
  })

  const debouncedSearch = useDebounceFn(() => {
    if (searchCriteria.value) {
      search.performSearch().then(() => {
        map.navigateTo = undefined
        if (search.storeOrOutlet && !search.storeOrOutlet.isBrand) map.selectedFeatureId = search.storeOrOutlet.id
      })
    }
  }, 500)

  olMapRef.on('moveend', () => {
    if (isPinCentering.value) return

    const view = olMapRef.getView()
    const center = view.getCenter()!
    const zoom = view.getZoom()!
    const extent = view.calculateExtent(olMapRef.getSize())
    map.onMoveEnd(center, extent, zoom)
    debouncedSearch()
  })
})

const searchCriteria = computed(() => {
  return !map.selectedFeatureId && (map.navigateTo || ui.showResultsAsMapMoves)
})

watch(
  [() => map.navigateTo, () => map.zoom],
  ([to, zoom], [oldTo, oldZoom]) => {
    if (!olMapRef) return
    if (to) olMapRef.getView().animate({ center: to, duration: 1000 })
    if (zoom !== oldZoom) olMapRef.getView().animate({ duration: 1000, zoom })
  },
  { flush: 'post' }
)

watch(
  () => [...search.stores],
  stores => {
    if (!olMapRef) return
    clusterSource.getSource()?.clear()
    const features: Feature[] = stores
      .filter(s => s.isProfileNavigable && s.truRating?.length > 0 && s.ratingCount > 0)
      .map(store => {
        const f = new Feature({ geometry: new Point([store.long, store.lat]) })
        f.set('name', store.displayName)
        f.setId(store.id)
        return f
      })
    clusterSource.getSource()?.addFeatures(features)
  }
)

watch(
  () => map.selectedFeatureId,
  (id, prevId) => {
    if (id) {
      const store = search.getStoreById(id)
      if (store) {
        isPinCentering.value = true

        olMapRef.getView().animate({
          center: [store.long, store.lat],
          duration: 500,
          zoom: map.zoom
        })

        // Position overlay AFTER animation completes to avoid autoPan
        // fighting the centering animation
        const onAnimEnd = () => {
          olMapRef.un('moveend', onAnimEnd)
          if (isDesktop) {
            const el = document.getElementById('map-popover')
            if (el) {
              storeSelectedOverlay.setElement(el)

              const pixel = olMapRef.getPixelFromCoordinate([store.long, store.lat])
              const features = olMapRef.getFeaturesAtPixel(pixel)
              const clustered = features?.[0]?.get('features')?.length > 1

              storeSelectedOverlay.setPositioning('bottom-center')
              storeSelectedOverlay.setOffset([clustered ? -6 : 0, clustered ? 21 : 27])
              storeSelectedOverlay.setPosition([store.long, store.lat])
            }
          }
          isPinCentering.value = false
        }
        olMapRef.on('moveend', onAnimEnd)
      }
    }

    if (!id && prevId) {
      const feature = clusterSource.getSource()!.getFeatureById(String(prevId)) as Feature
      if (feature) feature.setStyle(getMapMarker('map_marker'))
    }
  }
)

function initMap(center: MapCoordinate, zoom: number) {
  return new Map({
    target: document.getElementById('tru-map')!,
    layers: [new TileLayer({ source: new OSM() }), vectorClusterLayer],
    view: new View({ center, zoom, constrainResolution: true, maxZoom: 18 }),
    controls: defaultControls(),
    interactions: defaultInteractions().extend([new LinkInteraction({ params: ['x', 'y', 'z'] })])
  })
}

function getFeatureData<T = any>(feature: Feature, key: string) {
  return feature.get(key) as T
}

function isClusteredFeature(feature: Feature | FeatureLike) {
  return getFeatureData<boolean>(feature as Feature, 'isCluster')
}

function getFeature(feature: Feature | FeatureLike) {
  return getFeatures(feature)[0]
}

function getFeatures(feature: Feature | FeatureLike) {
  return getFeatureData<Array<Feature>>(feature as Feature, 'features')
}

function getMapMarker(name: string) {
  const styles: Record<string, Style> = {}
  ;['map_marker', 'map_marker_selected', 'map_marker_cluster', 'map_marker_cluster_selected'].forEach(marker => {
    styles[marker] = new Style({
      image: new Icon({
        size: marker.includes('cluster') ? [48, 48] : [22, 28],
        src: `/images/${marker}.svg`
      })
    })
  })
  return styles[name]
}
</script>
